import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marketing } from '../services/api';
import { Loader2, ChevronLeft, Users, MessageCircle } from 'lucide-react';

// Owner view of a single survey's responses — one row per submission.
export default function SurveyResponses() {
  const { sid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    marketing.surveyResponses(sid).then(setData).catch(e => setErr(e?.response?.data?.detail || 'Not found'));
  }, [sid]);

  if (err) return <div className="pbb-card p-6 text-gray-300" data-testid="survey-responses-error">{err}</div>;
  if (!data) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const { survey, responses } = data;
  const qmap = Object.fromEntries((survey.questions || []).map(q => [q.id, q]));

  return (
    <div className="space-y-4" data-testid="survey-responses-page">
      <Link to="/marketing/surveys" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1"><ChevronLeft size={14}/> Back to surveys</Link>
      <div>
        <h1 className="text-2xl font-bold">{survey.title}</h1>
        <p className="text-gray-400 text-sm inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><Users size={12}/> {responses.length} responses</span>
          <span className="inline-flex items-center gap-1"><MessageCircle size={12}/> {survey.questions?.length || 0} questions</span>
        </p>
      </div>
      {responses.length === 0 ? (
        <div className="pbb-card p-8 text-center" data-testid="survey-responses-empty">
          <MessageCircle className="mx-auto text-gray-500" size={26}/>
          <div className="mt-2 font-semibold text-white">No responses yet</div>
          <p className="text-sm text-gray-400 mt-1">Share your survey — every completion shows up here.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="survey-responses-list">
          {responses.map(r => (
            <li key={r.id} className="pbb-card p-4" data-testid={`survey-response-row-${r.id}`}>
              <div className="text-xs text-gray-500 mb-2">{new Date(r.createdAt).toLocaleString()} · <span className="text-gray-300">{r.email}</span></div>
              <ul className="space-y-1.5 text-sm">
                {(r.answers || []).map((a, i) => {
                  const q = qmap[a.questionId];
                  return (
                    <li key={i} className="text-gray-200">
                      <div className="text-[11px] text-gray-500 uppercase tracking-wider">{q?.prompt || '(unknown question)'}</div>
                      <div className="text-white">{Array.isArray(a.value) ? a.value.join(', ') : String(a.value ?? '—')}</div>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
